import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    // Attach pagination click handler first (document-level so it always receives clicks)
    this.#attachPaginationClickHandler();

    // Defer bar setup so DOM/refs are ready; update state when bar is present
    requestAnimationFrame(() => {
      this.#setupPaginationBar();
    });

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onPaginationClick, true);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /** @type {number} */
  #currentPage = 1;

  /** @type {(event: MouseEvent) => void} */
  #onPaginationClick;

  /**
   * Get current page from URL (e.g. ?page=2)
   */
  #getCurrentPageFromURL() {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('page');
    const num = p ? parseInt(p, 10) : 1;
    return Number.isNaN(num) || num < 1 ? 1 : num;
  }

  /**
   * Resolve a ref by name (component refs or querySelector fallback)
   * @param {string} name
   * @returns {HTMLElement | undefined}
   */
  #getRef(name) {
    return this.refs[name] ?? this.querySelector(`[ref="${name}"]`) ?? undefined;
  }

  /**
   * Get total number of pages from grid or pagination bar
   */
  #getLastPage() {
    const paginationBar = this.#getRef('paginationBar');
    const grid = this.#getRef('grid');
    const fromBar = paginationBar?.getAttribute?.('data-last-page');
    if (fromBar) return parseInt(fromBar, 10) || 1;
    const fromGrid = grid?.getAttribute?.('last-page');
    if (fromGrid) return parseInt(fromGrid, 10) || 1;
    return 1;
  }

  /**
   * Attach document-level click handler for pagination so clicks are always received
   * (avoids refs/timing issues; only handles clicks inside this component).
   */
  #attachPaginationClickHandler() {
    this.#onPaginationClick = (event) => {
      if (!this.isConnected || !this.contains(event.target)) return;
      const lastPage = this.#getLastPage();
      if (event.target.closest('[ref="prevPage"]')) {
        event.preventDefault();
        if (this.#currentPage > 1) this.#goToPage(this.#currentPage - 1);
        return;
      }
      if (event.target.closest('[ref="nextPage"]')) {
        event.preventDefault();
        if (this.#currentPage < lastPage) this.#goToPage(this.#currentPage + 1);
        return;
      }
      const pageLink = event.target.closest('a[data-page]');
      if (pageLink) {
        event.preventDefault();
        const pageNum = parseInt(pageLink.getAttribute('data-page'), 10);
        if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= lastPage) {
          this.#goToPage(pageNum);
        }
      }
    };
    document.addEventListener('click', this.#onPaginationClick, true);
  }

  /**
   * Cache the currently visible section HTML so Previous / back-to-page-1 is instant (no fetch).
   * Called on load so we never have to re-fetch the initial page.
   */
  #cacheCurrentPageHTML() {
    const section = this.closest('.shopify-section');
    if (!section?.id) return;
    const html = section.outerHTML;
    if (html) this.pages.set(this.#currentPage, html);
  }

  /**
   * Prefetch adjacent pages in the background so Next/Previous feel instant.
   * Prefetch previous page first when not on page 1 so "Previous" is more likely cached.
   */
  #prefetchAdjacentPages() {
    const lastPage = this.#getLastPage();
    const next = this.#currentPage + 1;
    const prev = this.#currentPage - 1;
    if (prev >= 1 && !this.pages.has(prev)) this.#fetchSpecificPage(prev, undefined);
    if (next <= lastPage && !this.pages.has(next)) this.#fetchSpecificPage(next, undefined);
  }

  /**
   * Setup pagination bar state (current page, last page, prev/next disabled, page numbers).
   * Call after DOM is ready; bar may not exist yet on first load.
   * Cache initial page twice so Previous is always instant (second run catches late-rendered section).
   */
  #setupPaginationBar() {
    this.#currentPage = this.#getCurrentPageFromURL();
    const lastPage = this.#getLastPage();
    this.#cacheCurrentPageHTML();
    this.#updatePaginationBarState(lastPage);
    this.#prefetchAdjacentPages();
    requestIdleCallback(() => this.#cacheCurrentPageHTML(), { timeout: 150 });
  }

  /** Max number of page number links to show (e.g. 1 ... 4 5 6 ... 10) */
  static #MAX_VISIBLE_PAGES = 7;

  /**
   * Build list of page numbers (and ellipsis) to show for limited pagination
   * @param {number} current
   * @param {number} lastPage
   * @returns {Array<number|'ellipsis'>}
   */
  static #getVisiblePageNumbers(current, lastPage) {
    const max = PaginatedList.#MAX_VISIBLE_PAGES;
    if (lastPage <= max) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }
    const out = [];
    const half = Math.floor(max / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(lastPage, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    if (start > 1) {
      out.push(1);
      if (start > 2) out.push('ellipsis');
    }
    for (let i = start; i <= end; i++) out.push(i);
    if (end < lastPage) {
      if (end < lastPage - 1) out.push('ellipsis');
      out.push(lastPage);
    }
    return out;
  }

  /**
   * Update prev/next disabled state, page indicator, and page number links
   */
  /**
   * Build URL for a given page (keeps current path and query params, sets page)
   * @param {number} page
   * @returns {string}
   */
  #getPageURL(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return url.toString();
  }

  #updatePaginationBarState(lastPage) {
    const prevPage = this.#getRef('prevPage');
    const nextPage = this.#getRef('nextPage');
    const paginationBar = this.#getRef('paginationBar');
    const pageNumbersEl = this.#getRef('pageNumbers');

    if (prevPage) prevPage.disabled = this.#currentPage <= 1;
    if (nextPage) nextPage.disabled = this.#currentPage >= lastPage;
    if (paginationBar) paginationBar.setAttribute('data-current-page', String(this.#currentPage));

    if (pageNumbersEl && lastPage > 1) {
      const parts = PaginatedList.#getVisiblePageNumbers(this.#currentPage, lastPage);
      pageNumbersEl.innerHTML = parts
        .map((p) => {
          if (p === 'ellipsis') {
            return '<span class="pagination-bar__ellipsis" aria-hidden="true">…</span>';
          }
          const isCurrent = p === this.#currentPage;
          const href = this.#getPageURL(p);
          return isCurrent
            ? `<span class="pagination-bar__page--current" aria-current="page">${p}</span>`
            : `<a href="${href}" data-page="${p}" class="pagination-bar__page">${p}</a>`;
        })
        .join('');
    }
  }

  /**
   * Go to a specific page. Same flow for Next and Previous: cache current page, fetch target if missing, then update state, DOM, and post-update tasks.
   */
  async #goToPage(pageNum) {
    const grid = this.#getRef('grid');
    if (!grid) return;

    const lastPage = this.#getLastPage();
    if (pageNum < 1 || pageNum > lastPage) return;
    if (pageNum === this.#currentPage) return;

    // Always cache the page we're leaving *before* changing DOM so Previous is as instant as Next
    this.#cacheCurrentPageHTML();

    const usedCachedPage = this.pages.has(pageNum);
    if (!usedCachedPage) {
      const ok = await this.#fetchSpecificPage(pageNum, undefined);
      if (!ok) return;
    }

    const items = this.#getGridForPage(pageNum);
    if (!items || items.length === 0) return;

    this.#currentPage = pageNum;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNum));
    url.hash = '';
    history.pushState('', '', url.toString());
    this.#updatePaginationBarState(lastPage);

    grid.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const node of items) fragment.appendChild(node);
    grid.appendChild(fragment);

    this.#cacheCurrentPageHTML();
    this.#prefetchAdjacentPages();
    this.#processNewElements();
    this.#reflowInjectedOverflowLists(grid, usedCachedPage);

    this.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  /**
   * After injecting new cards, fix swatch layout so overflow-list keeps swatches horizontal.
   * Next (fetched): only rAF + idle – do not add delayed reflow.
   * Previous (cached): add one delayed reflow at 80ms so layout is ready (sync path).
   */
  #reflowInjectedOverflowLists(grid, usedCachedPage = false) {
    const overflowLists = Array.from(grid.querySelectorAll('overflow-list'));
    if (overflowLists.length === 0) return;
    overflowLists.forEach((el) => el.removeAttribute('defer'));

    const triggerReflow = () => {
      overflowLists.forEach((el) => {
        if (el.isConnected) {
          el.dispatchEvent(new CustomEvent('reflow', { bubbles: false, detail: { lastVisibleElement: null } }));
        }
      });
    };

    requestAnimationFrame(triggerReflow);
    requestIdleCallback(() => requestAnimationFrame(triggerReflow), { timeout: 100 });
    if (usedCachedPage) {
      setTimeout(() => requestAnimationFrame(triggerReflow), 80);
    }
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    return true;
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @returns {Promise<boolean>} Whether the fetch succeeded and was cached
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return false;
    try {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
      this.pages.set(pageNumber, pageContent);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios (deferred so paint is not blocked)
   */
  #processNewElements() {
    requestIdleCallback(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const rawId = this.sectionId && String(this.sectionId).replace(/^shopify-section-/, '');
    let sectionEl = rawId ? parsedPage.getElementById(`shopify-section-${rawId}`) : null;
    if (!sectionEl && parsedPage.body?.firstElementChild?.id?.startsWith?.('shopify-section-')) {
      sectionEl = parsedPage.body.firstElementChild;
    }
    const scope = sectionEl || parsedPage.body;
    const gridElement = scope.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const cards = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    return cards.length > 0 ? cards : undefined;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages (Option A: no prefetch)
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#currentPage = 1;
    const lastPage = this.#getLastPage();
    this.#updatePaginationBarState(lastPage);
  };
}
